import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { EventTimeline, MatrixEvent, MatrixEventEvent, RoomEvent, RoomStateEvent } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import logger from '../utils/logger';
import { MAX_PINS_PER_ROOM } from '../constants/limits';

// Number of pinned messages to load initially and per "load more" action
const INITIAL_LOAD_COUNT = 5;

// Module-level shared caches that persist across all hook instances
// This ensures all components using usePinnedMessages see the same edits/redactions
const sharedEditContentCache = new Map(); // eventId -> edited content (m.new_content)
const sharedRedactedEventsSet = new Set(); // Set of redacted event IDs

// Module-level shared cache for pinnedEventIds per room
// This ensures all components (MessageBubble, Banner, Panel) see the same pinned state
// without waiting for useEffect to run after mount
const sharedPinnedIdsCache = new Map(); // roomId -> Set of pinned event IDs

// Module-level: per-room auto-unpin promise for cross-instance deduplication
// Prevents multiple hook instances from sending duplicate state events
const autoUnpinInFlightMap = new Map(); // roomId -> Promise

// Retry configuration for pin/unpin race condition recovery (NED-3201)
// When two users pin simultaneously, the second write overwrites the first.
// After sending, we verify our change survived via sync; if not, re-merge and retry.
const MAX_PIN_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 200;
const SYNC_WAIT_TIMEOUT_MS = 5000;

// Per-room serialization of pin/unpin operations from this client
// Prevents intra-client race conditions (e.g., rapid pin + unpin)
const pinOperationInFlightMap = new Map(); // roomId -> Promise

/**
 * Detect if event content represents an edit of a pinned message.
 * @param {Object} content - Pre-extracted event content (getClearContent || getContent)
 * @param {Set} pinnedIdsSet - Set of pinned event IDs
 * @returns {{ editedEventId: string, newContent: Object } | null}
 */
const getEditOfPinnedEvent = (content, pinnedIdsSet) => {
  const relatesTo = content['m.relates_to'];
  if (relatesTo?.rel_type !== 'm.replace') return null;
  const editedEventId = relatesTo.event_id;
  const newContent = content['m.new_content'];
  return (editedEventId && pinnedIdsSet.has(editedEventId) && newContent)
    ? { editedEventId, newContent }
    : null;
};

/**
 * Detect if an event is a redaction of a pinned message.
 * @param {Object} event - The Matrix event (for getAssociatedId)
 * @param {Object} content - Pre-extracted event content (getClearContent || getContent)
 * @param {Set} pinnedIdsSet - Set of pinned event IDs
 * @returns {string | null} - The redacted pinned event ID, or null
 */
const getRedactedPinnedEventId = (event, content, pinnedIdsSet) => {
  const redactedEventId = event.getAssociatedId?.() || content.redacts;
  return (redactedEventId && pinnedIdsSet.has(redactedEventId))
    ? redactedEventId
    : null;
};

/**
 * Reset all module-level caches. Used for testing to ensure test isolation.
 * @private - Only exported for testing purposes
 */
export const __resetCachesForTesting = () => {
  sharedEditContentCache.clear();
  sharedRedactedEventsSet.clear();
  sharedPinnedIdsCache.clear();
  autoUnpinInFlightMap.clear();
  pinOperationInFlightMap.clear();
};

/**
 * Module-level auto-unpin: removes redacted event IDs from server pin state.
 * Deduplicates across all hook instances via autoUnpinInFlightMap.
 * If already in-flight for a room, returns existing promise (simple skip).
 *
 * Note: "simple skip" dedup — if a second redaction arrives while in-flight,
 * its ID may not be cleaned from server state until the next auto-unpin pass.
 * Acceptable because UI is already correct via sharedRedactedEventsSet;
 * server cleanup is best-effort. pinEvent/unpinEvent also defensively filter.
 *
 * @param {Object} client - Matrix client
 * @param {string} roomId - Room ID
 * @returns {Promise|undefined} - The in-flight promise, or undefined if skipped
 */
const autoUnpinRedactedEvent = (client, roomId) => {
  if (autoUnpinInFlightMap.has(roomId)) {
    return autoUnpinInFlightMap.get(roomId);
  }

  const room = client.getRoom(roomId);
  if (!room) return;

  const pinnedEvent = room.currentState?.getStateEvents('m.room.pinned_events', '');
  const currentPins = pinnedEvent?.getContent()?.pinned || [];
  const cleanedPins = currentPins.filter(id => !sharedRedactedEventsSet.has(id));

  if (cleanedPins.length === currentPins.length) return; // already clean

  const removedIds = currentPins.filter(id => sharedRedactedEventsSet.has(id));
  logger.info('[usePinnedMessages] Auto-unpinning redacted events, removing',
    removedIds.length, 'stale IDs:', removedIds);

  const promise = client.sendStateEvent(roomId, 'm.room.pinned_events', { pinned: cleanedPins }, '')
    .catch(err => logger.warn('[usePinnedMessages] Failed to auto-unpin:', err))
    .finally(() => {
      if (autoUnpinInFlightMap.get(roomId) === promise) {
        autoUnpinInFlightMap.delete(roomId);
      }
    });

  autoUnpinInFlightMap.set(roomId, promise);
  return promise;
};

/**
 * Wait for the next m.room.pinned_events state event to arrive via sync.
 * Returns the new pinned IDs array, or null if timed out.
 *
 * @param {Object} room - Matrix Room object
 * @param {number} timeoutMs - Maximum wait time
 * @returns {Promise<string[]|null>} New pinned IDs or null on timeout
 */
const waitForPinnedStateSync = (room, timeoutMs = SYNC_WAIT_TIMEOUT_MS) => {
  return new Promise((resolve) => {
    let resolved = false;

    const done = (value) => {
      if (resolved) return;
      resolved = true;
      room.off(RoomStateEvent.Events, handler);
      resolve(value);
    };

    const handler = (event) => {
      if (event.getType() === 'm.room.pinned_events') {
        const ids = event.getContent()?.pinned || [];
        done(Array.isArray(ids) ? ids : []);
      }
    };

    room.on(RoomStateEvent.Events, handler);

    setTimeout(() => done(null), timeoutMs);
  });
};

/**
 * Send m.room.pinned_events state with post-send verification and retry.
 * After sending, waits for sync to confirm the server state includes our change.
 * If our change was overwritten by a concurrent write, re-reads state, merges, and retries.
 *
 * @param {Object} client - Matrix client
 * @param {string} roomId - Room ID
 * @param {string[]} initialIds - The pinned IDs array to send on the first attempt
 * @param {string} targetEventId - The event ID we're pinning or unpinning
 * @param {'pin'|'unpin'} operation - Whether we're pinning or unpinning
 * @param {Object} isMountedRef - Ref to check if component is still mounted
 * @returns {Promise<void>}
 */
const sendPinnedStateWithRetry = async (client, roomId, initialIds, targetEventId, operation, isMountedRef) => {
  let newIds = initialIds;

  for (let attempt = 0; attempt <= MAX_PIN_RETRIES; attempt++) {
    if (attempt > 0) {
      // Exponential backoff with jitter
      const delay = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1) + Math.random() * 100;
      logger.info(`[usePinnedMessages] Retry ${attempt}/${MAX_PIN_RETRIES} for ${operation} ${targetEventId} after ${Math.round(delay)}ms`);
      await new Promise(r => setTimeout(r, delay));

      // Abort if component unmounted
      if (!isMountedRef.current) {
        logger.info('[usePinnedMessages] Component unmounted during retry, aborting');
        return;
      }

      // Re-read current state and merge our intent
      const room = client.getRoom(roomId);
      if (!room) {
        logger.warn('[usePinnedMessages] Room no longer accessible during retry, aborting');
        return;
      }

      const pinnedEvent = room.currentState?.getStateEvents('m.room.pinned_events', '');
      const currentIds = pinnedEvent?.getContent()?.pinned || [];
      const freshIds = Array.isArray(currentIds) ? currentIds : [];

      if (operation === 'pin') {
        if (freshIds.includes(targetEventId)) {
          logger.info(`[usePinnedMessages] ${targetEventId} already present after concurrent update, no retry needed`);
          return;
        }
        // Filter redacted, add our target
        const activeIds = freshIds.filter(id => !sharedRedactedEventsSet.has(id));
        // Re-check pin limit with merged state
        if (activeIds.length >= MAX_PINS_PER_ROOM) {
          const error = new Error('PIN_LIMIT_EXCEEDED');
          error.code = 'PIN_LIMIT_EXCEEDED';
          throw error;
        }
        newIds = [...activeIds, targetEventId];
      } else {
        if (!freshIds.includes(targetEventId)) {
          logger.info(`[usePinnedMessages] ${targetEventId} already absent after concurrent update, no retry needed`);
          return;
        }
        newIds = freshIds.filter(id => id !== targetEventId && !sharedRedactedEventsSet.has(id));
      }
    }

    // Set up sync listener BEFORE sending to avoid missing the event
    const room = client.getRoom(roomId);
    if (!room) throw new Error('Room not found');
    const syncPromise = waitForPinnedStateSync(room);

    // Send the state event
    await client.sendStateEvent(roomId, 'm.room.pinned_events', { pinned: newIds }, '');

    // Wait for sync to confirm
    const syncedIds = await syncPromise;

    if (syncedIds === null) {
      // Timed out waiting for sync — best effort, assume success
      logger.warn(`[usePinnedMessages] Timed out waiting for sync after ${operation} ${targetEventId}`);
      return;
    }

    // Verify our change survived
    const verified = operation === 'pin'
      ? syncedIds.includes(targetEventId)
      : !syncedIds.includes(targetEventId);

    if (verified) {
      // Check if sync echo differs from what we sent — indicates concurrent activity
      const syncDiffers = syncedIds.length !== newIds.length ||
        syncedIds.some(id => !newIds.includes(id));

      if (syncDiffers) {
        // Another write happened concurrently — wait briefly and re-verify
        await new Promise(r => setTimeout(r, RETRY_BASE_DELAY_MS));

        const latestPinnedEvent = room.currentState?.getStateEvents('m.room.pinned_events', '');
        const latestIds = latestPinnedEvent?.getContent()?.pinned || [];
        const stillVerified = operation === 'pin'
          ? latestIds.includes(targetEventId)
          : !latestIds.includes(targetEventId);

        if (!stillVerified) {
          logger.warn(`[usePinnedMessages] ${operation} ${targetEventId} passed initial verification but was overwritten by concurrent write`);
          continue;
        }
      }

      if (attempt > 0) {
        logger.info(`[usePinnedMessages] ${operation} ${targetEventId} verified after ${attempt} retry(ies)`);
      }
      return;
    }

    logger.warn(`[usePinnedMessages] ${operation} ${targetEventId} was overwritten by concurrent update (attempt ${attempt + 1}/${MAX_PIN_RETRIES + 1})`);
  }

  // Exhausted retries — don't throw, state will self-correct on next sync
  logger.error(`[usePinnedMessages] ${operation} ${targetEventId} failed after ${MAX_PIN_RETRIES} retries — concurrent writes keep overwriting`);
};

/**
 * Check if an event is pinned in a room by checking the shared cache directly.
 * This is useful for components that need immediate access to pinned state
 * without waiting for React state updates.
 *
 * Falls back to reading from room state if cache is not populated.
 *
 * @param {string} roomId - The room ID to check
 * @param {string} eventId - The event ID to check
 * @param {Object} client - Optional Matrix client for room state fallback
 * @returns {boolean} - True if the event is pinned
 */
export const isEventPinnedInCache = (roomId, eventId, client = null) => {
  if (!roomId || !eventId) {
    return false;
  }

  // First check the shared cache
  const cache = sharedPinnedIdsCache.get(roomId);

  if (cache) {
    return cache.has(eventId);
  }

  // If cache is empty but we have a client, try to read from room state
  // and populate the cache for future lookups
  if (client) {
    try {
      const room = client.getRoom(roomId);
      if (room) {
        const pinnedEvent = room.currentState.getStateEvents('m.room.pinned_events', '');
        const ids = pinnedEvent?.getContent()?.pinned || [];
        if (Array.isArray(ids) && ids.length > 0) {
          // Populate cache for future lookups
          sharedPinnedIdsCache.set(roomId, new Set(ids));
          return ids.includes(eventId);
        }
      }
    } catch {
      // Ignore errors, return false
    }
  }

  return false;
};

/**
 * Hook for managing pinned messages in a Matrix room.
 * Provides state for pinned event IDs and full events, loading state,
 * permission checking, and pin/unpin mutation functions.
 *
 * @param {string} roomId - The room ID to manage pinned messages for
 * @returns {Object} Pinned messages state and actions
 */
export const usePinnedMessages = (roomId) => {
  const client = useMatrixClient();

  // Helper to read pinned IDs from room state synchronously
  // This is used for lazy initialization to avoid flash of unpinned state
  const getInitialPinnedIds = () => {
    // First, check the shared cache for this room
    // This ensures all component instances see the same state immediately
    if (roomId && sharedPinnedIdsCache.has(roomId)) {
      return Array.from(sharedPinnedIdsCache.get(roomId));
    }

    // If not in cache, read from room state
    if (!client || !roomId) {
      return [];
    }
    const room = client.getRoom(roomId);
    if (!room) {
      return [];
    }
    try {
      const pinnedEvent = room.currentState.getStateEvents('m.room.pinned_events', '');
      const ids = pinnedEvent?.getContent()?.pinned || [];
      const result = Array.isArray(ids) ? ids : [];

      // Populate shared cache for other components
      if (roomId && result.length > 0) {
        sharedPinnedIdsCache.set(roomId, new Set(result));
      }

      return result;
    } catch {
      return [];
    }
  };

  // State management with lazy initialization for pinnedEventIds
  // This ensures the first render already has the correct pinned state,
  // preventing flash of unpinned state when components remount after scrolling
  const [pinnedEventIds, setPinnedEventIds] = useState(getInitialPinnedIds);
  const [pinnedEvents, setPinnedEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [canPin, setCanPin] = useState(false);
  const [displayedCount, setDisplayedCount] = useState(INITIAL_LOAD_COUNT);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Cache for loaded events to avoid redundant fetches
  const eventCacheRef = useRef(new Map());

  // Track if component is mounted to prevent state updates after unmount
  const isMountedRef = useRef(true);

  // Suppress loadPinnedIds during retry to prevent UI flash (NED-3201)
  const retryInProgressRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load pinned event IDs from room state and set up real-time listener
  useEffect(() => {
    if (!client || !roomId) {
      setPinnedEventIds([]);
      setPinnedEvents([]);
      setIsLoading(false);
      setCanPin(false);
      return;
    }

    const room = client.getRoom(roomId);
    if (!room) {
      setPinnedEventIds([]);
      setPinnedEvents([]);
      setIsLoading(false);
      setCanPin(false);
      return;
    }

    /**
     * Load pinned event IDs from m.room.pinned_events state event
     */
    const loadPinnedIds = () => {
      try {
        const pinnedEvent = room.currentState.getStateEvents('m.room.pinned_events', '');
        const ids = pinnedEvent?.getContent()?.pinned || [];

        if (isMountedRef.current) {
          setPinnedEventIds(Array.isArray(ids) ? ids : []);
        }
      } catch (err) {
        logger.error('Error loading pinned event IDs:', err);
        if (isMountedRef.current) {
          setError(err);
        }
      }
    };

    /**
     * Check if current user can pin/unpin messages
     * by checking power levels against m.room.pinned_events requirement
     */
    const checkCanPin = () => {
      try {
        const userId = client.getUserId();
        const powerLevelsEvent = room
          .getLiveTimeline()
          .getState(EventTimeline.FORWARDS)
          .getStateEvents('m.room.power_levels', '');

        if (!powerLevelsEvent) {
          if (isMountedRef.current) {
            setCanPin(false);
          }
          return;
        }

        const powerLevels = powerLevelsEvent.getContent();

        // Get the required level for m.room.pinned_events
        // Falls back to state_default if not specifically set
        const requiredLevel = powerLevels.events?.['m.room.pinned_events']
          ?? powerLevels.state_default
          ?? 50;

        // Get current user's power level
        const userPowerLevel = powerLevels.users?.[userId]
          ?? powerLevels.users_default
          ?? 0;

        if (isMountedRef.current) {
          setCanPin(userPowerLevel >= requiredLevel);
        }
      } catch (err) {
        logger.error('Error checking pin permission:', err);
        if (isMountedRef.current) {
          setCanPin(false);
        }
      }
    };

    // Load initial state
    loadPinnedIds();
    checkCanPin();

    /**
     * Handle room state events for real-time updates
     */
    const handleStateEvents = (event) => {
      const eventType = event.getType();

      if (eventType === 'm.room.pinned_events') {
        // During retry, don't let sync overwrite optimistic state (NED-3201)
        if (!retryInProgressRef.current) {
          logger.info('Pinned events changed, reloading');
          loadPinnedIds();
        } else {
          logger.debug('[usePinnedMessages] Suppressing loadPinnedIds during retry');
        }
      }

      if (eventType === 'm.room.power_levels') {
        logger.info('Power levels changed, rechecking pin permission');
        checkCanPin();
      }
    };

    room.on(RoomStateEvent.Events, handleStateEvents);

    return () => {
      room.off(RoomStateEvent.Events, handleStateEvents);
    };
  }, [client, roomId]);

  // Fetch full event content for each pinned event ID
  useEffect(() => {
    if (!client || !roomId || pinnedEventIds.length === 0) {
      setPinnedEvents([]);
      setIsLoading(false);
      return;
    }

    const room = client.getRoom(roomId);
    if (!room) {
      setPinnedEvents([]);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    /**
     * Scan the room's timeline for edit events that target pinned messages.
     * This catches edits that arrived before the timeline listener was set up.
     */
    const scanTimelineForEdits = () => {
      const pinnedIdsSet = new Set(pinnedEventIds);
      const timeline = room.getLiveTimeline?.();
      if (!timeline) return;

      const timelineEvents = timeline.getEvents?.() || [];
      for (const event of timelineEvents) {
        const content = event?.getClearContent?.() || event?.getContent?.() || {};

        const edit = getEditOfPinnedEvent(content, pinnedIdsSet);
        if (edit) {
          // Always overwrite: timeline is chronological, so the last edit for a
          // given event is the most recent. Skipping when the cache already has an
          // entry would keep a stale first-edit when the cache was cleared on
          // remount while a newer edit exists in the timeline (NED-3156).
          sharedEditContentCache.set(edit.editedEventId, edit.newContent);
        }

        if (event?.getType?.() === 'm.room.redaction') {
          const redactedId = getRedactedPinnedEventId(event, content, pinnedIdsSet);
          if (redactedId && !sharedRedactedEventsSet.has(redactedId)) {
            logger.info('[usePinnedMessages] Found existing redaction for pinned message:', redactedId);
            sharedRedactedEventsSet.add(redactedId);
          }
        }
      }
    };

    const fetchEvents = async () => {
      if (cancelled) return;

      if (isMountedRef.current) {
        setIsLoading(true);
        setError(null);
      }

      // Scan timeline for existing edits/redactions before loading events
      scanTimelineForEdits();

      // Use Promise.allSettled for fault-tolerant fetching
      // This ensures partial failures don't break the entire list
      const createMissingEvent = (id, error = 'EVENT_NOT_FOUND') => ({
        id, missing: true, error,
      });

      const results = await Promise.allSettled(
        pinnedEventIds.map(async (eventId) => {
          // Check cache first
          if (eventCacheRef.current.has(eventId)) {
            return eventCacheRef.current.get(eventId);
          }

          try {
            // Try to find in room's timeline first
            let event = room.findEventById?.(eventId) || room.getEventById?.(eventId);

            // If not found, try to fetch from server
            if (!event) {
              const fetchedEvent = await client.fetchRoomEvent?.(roomId, eventId).catch(() => null);

              if (!fetchedEvent) {
                return createMissingEvent(eventId);
              }

              // After fetching, the event should be available in the room's timeline
              event = room.findEventById?.(eventId) || room.getEventById?.(eventId);

              // If still not available, wrap raw JSON in MatrixEvent for SDK method compatibility
              // fetchRoomEvent returns plain JSON without getClearContent/getContent/getSender methods
              if (!event) {
                const matrixEvent = new MatrixEvent(fetchedEvent);
                // Attempt decryption if keys are available (e.g. recovery key was entered)
                if (matrixEvent.getType() === 'm.room.encrypted' && client.decryptEventIfNeeded) {
                  try {
                    await client.decryptEventIfNeeded(matrixEvent);
                  } catch {
                    // Keys not available yet — banner will show "Encrypted message"
                  }
                }
                eventCacheRef.current.set(eventId, matrixEvent);
                return matrixEvent;
              }
            }

            // Cache the result
            if (event) {
              eventCacheRef.current.set(eventId, event);
            }

            return event || createMissingEvent(eventId);
          } catch (err) {
            logger.warn(`Error loading pinned event ${eventId}:`, err);
            return createMissingEvent(eventId, err.message || 'FETCH_FAILED');
          }
        }),
      );

      // Process results from Promise.allSettled
      const events = results.map((result, index) => {
        if (result.status === 'rejected') {
          return createMissingEvent(pinnedEventIds[index], result.reason?.message || 'FETCH_FAILED');
        }
        return result.value;
      });

      if (cancelled || !isMountedRef.current) return;

      // Keep all events including missing ones for UI to handle gracefully
      setPinnedEvents(events);
      setIsLoading(false);
    };

    fetchEvents();

    return () => {
      cancelled = true;
    };
   
  }, [client, roomId, pinnedEventIds, refreshCounter]);

  // Track canPin in a ref so the timeline listener can read the latest value
  // without needing canPin in its dependency array (which would cause re-subscriptions)
  const canPinRef = useRef(canPin);
  canPinRef.current = canPin;

  // Listen for timeline events to detect edits and redactions of pinned messages
  useEffect(() => {
    logger.debug('[usePinnedMessages] Timeline listener effect running:', {
      hasClient: !!client,
      roomId,
      pinnedEventIdsCount: pinnedEventIds.length,
      pinnedEventIds,
    });

    if (!client || !roomId || pinnedEventIds.length === 0) {
      logger.debug('[usePinnedMessages] Timeline listener skipped - missing deps');
      return;
    }

    const room = client.getRoom(roomId);
    if (!room) {
      logger.debug('[usePinnedMessages] Timeline listener skipped - no room');
      return;
    }

    const pinnedIdsSet = new Set(pinnedEventIds);
    logger.debug('[usePinnedMessages] Timeline listener registered for room:', roomId, 'with pinned IDs:', Array.from(pinnedIdsSet));

    /**
     * Handle timeline events to detect edits and redactions of pinned messages
     */
    const handleTimelineEvent = (event, eventRoom) => {
      if (!isMountedRef.current) return;

      const eventRoomId = eventRoom?.roomId || event?.getRoomId?.();
      const eventType = event?.getType?.();

      logger.debug('[usePinnedMessages] Timeline event received:', {
        type: eventType,
        eventRoomId,
        targetRoomId: roomId,
        isTargetRoom: eventRoomId === roomId,
      });

      // Only handle events for this room
      if (eventRoomId !== roomId) {
        return;
      }

      const content = event.getClearContent?.() || event.getContent?.() || {};

      // Handle redaction events - message was deleted
      if (eventType === 'm.room.redaction') {
        const redactedId = getRedactedPinnedEventId(event, content, pinnedIdsSet);
        logger.debug('[usePinnedMessages] Redaction event:', { redactedId, isPinned: !!redactedId });
        if (redactedId) {
          logger.info('[usePinnedMessages] Pinned message was redacted, adding to shared redacted set');
          sharedRedactedEventsSet.add(redactedId);
          setRefreshCounter(c => c + 1);
          if (canPinRef.current) {
            autoUnpinRedactedEvent(client, roomId);
          }
        }
        return;
      }

      // Handle edit events - message was edited
      const edit = getEditOfPinnedEvent(content, pinnedIdsSet);
      if (edit) {
        logger.debug('[usePinnedMessages] Edit event:', { editedEventId: edit.editedEventId, hasNewContent: true });
        logger.info('[usePinnedMessages] Pinned message was edited, storing in shared edit cache');
        sharedEditContentCache.set(edit.editedEventId, edit.newContent);
        setRefreshCounter(c => c + 1);
      }
    };

    /**
     * Handle decrypted events to detect edits/redactions of pinned messages
     * that arrived encrypted. When a remote user edits a pinned message in an
     * encrypted room, RoomEvent.Timeline fires with still-encrypted content
     * (no m.relates_to). The SDK decrypts asynchronously and emits this event.
     * Without this listener, the pinned banner never updates (NED-3156).
     */
    const handleDecryptedEvent = (event) => {
      if (!isMountedRef.current) return;

      // Filter for target room
      if (event.getRoomId?.() !== roomId) return;

      // Skip decryption failures
      if (event.isDecryptionFailure?.()) return;

      const content = event.getClearContent?.() || event.getContent?.() || {};

      // Handle edit events
      const edit = getEditOfPinnedEvent(content, pinnedIdsSet);
      if (edit) {
        logger.info('[usePinnedMessages] Decrypted edit for pinned message:', edit.editedEventId);
        sharedEditContentCache.set(edit.editedEventId, edit.newContent);
        setRefreshCounter(c => c + 1);
        return;
      }

      // Handle redaction events
      if (event.getType?.() === 'm.room.redaction') {
        const redactedId = getRedactedPinnedEventId(event, content, pinnedIdsSet);
        if (redactedId) {
          logger.info('[usePinnedMessages] Decrypted redaction for pinned message:', redactedId);
          sharedRedactedEventsSet.add(redactedId);
          setRefreshCounter(c => c + 1);
          if (canPinRef.current) {
            autoUnpinRedactedEvent(client, roomId);
          }
        }
      }
    };

    client.on(RoomEvent.Timeline, handleTimelineEvent);
    client.on(MatrixEventEvent.Decrypted, handleDecryptedEvent);

    return () => {
      client.off(RoomEvent.Timeline, handleTimelineEvent);
      client.off(MatrixEventEvent.Decrypted, handleDecryptedEvent);
    };
  // autoUnpinRedactedEvent is module-level (not a dependency); canPinRef is a ref
  }, [client, roomId, pinnedEventIds]);

  // Clear cache entries for events that are no longer pinned
  useEffect(() => {
    const currentIds = new Set(pinnedEventIds);
    for (const cachedId of eventCacheRef.current.keys()) {
      if (!currentIds.has(cachedId)) {
        eventCacheRef.current.delete(cachedId);
      }
    }
  }, [pinnedEventIds]);

  // Sync pinnedEventIds to shared cache so all component instances see the same state
  // This is critical for components that mount after pins have been loaded (e.g., after scrolling)
  useEffect(() => {
    if (roomId && pinnedEventIds.length > 0) {
      sharedPinnedIdsCache.set(roomId, new Set(pinnedEventIds));
    } else if (roomId && pinnedEventIds.length === 0) {
      // Clear the cache for this room if no pinned messages
      sharedPinnedIdsCache.delete(roomId);
    }
  }, [roomId, pinnedEventIds]);

  // Track pinnedEventIds in a ref for cleanup (always has latest value)
  const pinnedEventIdsRef = useRef(pinnedEventIds);
  pinnedEventIdsRef.current = pinnedEventIds;

  // Clean up shared caches when room changes or hook unmounts to prevent memory leaks
  useEffect(() => {
    return () => {
      // Clear entries for this room's pinned events from shared caches
      // Uses ref to get the latest pinnedEventIds at cleanup time
      pinnedEventIdsRef.current.forEach(id => {
        sharedEditContentCache.delete(id);
        sharedRedactedEventsSet.delete(id);
      });
    };
  }, [roomId]); // Only re-run when room changes, cleanup old room's entries

  // Lazy loading: slice pinnedEvents by displayedCount and apply edits from shared cache
  const displayedEvents = useMemo(() => {
    const sliced = pinnedEvents.slice(0, displayedCount);

    // Apply edits and redactions from shared caches
    return sliced.map(event => {
      const eventId = event?.getId?.() || event?.event_id || event?.id;

      // Check if event was redacted (from shared set)
      // NOTE: Consumers MUST check the _sharedRedacted flag before accessing content
      // via getContent()/getClearContent(). Unlike edited events, the original content
      // methods are NOT overridden - they will return pre-redaction content if called.
      // See PinnedMessageTile.jsx for correct usage pattern.
      if (eventId && sharedRedactedEventsSet.has(eventId)) {
        // Create a wrapper that preserves methods but marks as redacted
        const wrappedEvent = Object.create(event);
        wrappedEvent._sharedRedacted = true;
        return wrappedEvent;
      }

      // Check if event was edited (from shared cache)
      if (eventId && sharedEditContentCache.has(eventId)) {
        const editedContent = sharedEditContentCache.get(eventId);
        // Create a wrapper that returns edited content
        const originalGetContent = event.getContent?.bind(event);
        const originalGetClearContent = event.getClearContent?.bind(event);

        const wrappedEvent = Object.create(event);
        wrappedEvent._isEdited = true;
        wrappedEvent._editedContent = editedContent;
        wrappedEvent.getContent = () => {
          const content = originalGetContent?.() || {};
          return { ...content, ...editedContent };
        };
        wrappedEvent.getClearContent = () => {
          const content = originalGetClearContent?.() || originalGetContent?.() || {};
          return { ...content, ...editedContent };
        };
        return wrappedEvent;
      }

      return event;
    });
  // Include refreshCounter to trigger re-computation when edits/redactions occur
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedEvents, displayedCount, refreshCounter]);

  // Check if there are more events to load
  const hasMore = useMemo(() => {
    return displayedCount < pinnedEvents.length;
  }, [displayedCount, pinnedEvents.length]);

  // Compute pinned count - uses pinnedEventIds as the source of truth for
  // immediate UI updates. This fixes a race condition where the banner count
  // wouldn't update until events were fetched, even though the state event
  // had already arrived. Excludes redacted messages since they show as
  // "unavailable" and shouldn't inflate the count.
  const pinnedCount = useMemo(() => {
    return pinnedEventIds.filter(eventId => !sharedRedactedEventsSet.has(eventId)).length;
  // Include refreshCounter to trigger re-computation when redactions occur
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinnedEventIds, refreshCounter]);

  /**
   * Load more pinned messages for lazy loading
   */
  const loadMore = useCallback(() => {
    setDisplayedCount(prev => prev + INITIAL_LOAD_COUNT);
  }, []);

  /**
   * Pin an event to the room
   * Uses optimistic update for immediate UI feedback with rollback on error
   * @param {string} eventId - The event ID to pin
   * @returns {Promise<void>}
   */
  const pinEvent = useCallback(async (eventId) => {
    if (!client || !roomId) {
      throw new Error('Cannot pin: client or roomId not available');
    }

    if (!canPin) {
      throw new Error('Cannot pin: insufficient permissions');
    }

    // Serialize pin/unpin operations for this room (NED-3201)
    const existingOp = pinOperationInFlightMap.get(roomId);
    if (existingOp) {
      await existingOp;
    }

    // Wait for any in-flight auto-unpin to complete before reading room state
    // This prevents a race where a pin state event clobbers the auto-unpin
    const inFlightAutoUnpin = autoUnpinInFlightMap.get(roomId);
    if (inFlightAutoUnpin) {
      await inFlightAutoUnpin;
    }

    // Read fresh room state to prevent race conditions in multi-user scenarios
    // This ensures we have the most up-to-date pinned list before checking limits
    const room = client.getRoom(roomId);
    const pinnedEvent = room?.currentState?.getStateEvents('m.room.pinned_events', '');
    const currentPinnedIds = pinnedEvent?.getContent()?.pinned || [];
    const freshPinnedIds = Array.isArray(currentPinnedIds) ? currentPinnedIds : [];

    // Filter out redacted events - they should not count toward the pin limit (NED-3119)
    const activePinnedIds = freshPinnedIds.filter(id => !sharedRedactedEventsSet.has(id));

    // Check if already pinned using active (non-redacted) data
    if (activePinnedIds.includes(eventId)) {
      logger.info('Event already pinned:', eventId);
      return;
    }

    // Check if pin limit reached using active (non-redacted) data
    if (activePinnedIds.length >= MAX_PINS_PER_ROOM) {
      const error = new Error('PIN_LIMIT_EXCEEDED');
      error.code = 'PIN_LIMIT_EXCEEDED';
      throw error;
    }

    // Store previous state for rollback (use React state for UI rollback)
    const previousIds = pinnedEventIds;
    // Build new IDs from active pins only (cleans stale redacted IDs from server)
    const newIds = [...activePinnedIds, eventId];

    // Optimistic update: immediately add to state
    setPinnedEventIds(newIds);

    const operationPromise = (async () => {
      retryInProgressRef.current = true;
      try {
        await sendPinnedStateWithRetry(client, roomId, newIds, eventId, 'pin', isMountedRef);
        logger.info('Pinned event:', eventId);
      } catch (err) {
        // Rollback on error
        logger.error('Error pinning event, rolling back:', err);
        if (isMountedRef.current) {
          setPinnedEventIds(previousIds);
        }
        throw err;
      } finally {
        retryInProgressRef.current = false;
        if (pinOperationInFlightMap.get(roomId) === operationPromise) {
          pinOperationInFlightMap.delete(roomId);
        }
      }
    })();

    pinOperationInFlightMap.set(roomId, operationPromise);
    return operationPromise;
  }, [client, roomId, canPin, pinnedEventIds]);

  /**
   * Unpin an event from the room
   * Uses optimistic update for immediate UI feedback with rollback on error
   * @param {string} eventId - The event ID to unpin
   * @returns {Promise<void>}
   */
  const unpinEvent = useCallback(async (eventId) => {
    if (!client || !roomId) {
      throw new Error('Cannot unpin: client or roomId not available');
    }

    if (!canPin) {
      throw new Error('Cannot unpin: insufficient permissions');
    }

    // Serialize pin/unpin operations for this room (NED-3201)
    const existingOp = pinOperationInFlightMap.get(roomId);
    if (existingOp) {
      await existingOp;
    }

    // Wait for any in-flight auto-unpin to complete before reading state
    const inFlightAutoUnpin = autoUnpinInFlightMap.get(roomId);
    if (inFlightAutoUnpin) {
      await inFlightAutoUnpin;
    }

    // Read fresh room state and filter redacted IDs (same pattern as pinEvent)
    // This prevents re-adding redacted IDs that autoUnpinRedactedEvent already cleaned
    const room = client.getRoom(roomId);
    const pinnedEvent = room?.currentState?.getStateEvents('m.room.pinned_events', '');
    const currentPinnedIds = pinnedEvent?.getContent()?.pinned || [];
    const freshPinnedIds = Array.isArray(currentPinnedIds) ? currentPinnedIds : [];

    // Check if actually pinned using fresh data
    if (!freshPinnedIds.includes(eventId)) {
      logger.info('Event not pinned:', eventId);
      return;
    }

    // Store previous state for rollback (use React state for UI rollback)
    const previousIds = pinnedEventIds;
    // Remove target ID and filter out any redacted IDs (NED-3119)
    const newIds = freshPinnedIds.filter(id => id !== eventId && !sharedRedactedEventsSet.has(id));

    // Optimistic update: immediately remove from state
    setPinnedEventIds(newIds);

    // Also remove from cache optimistically
    const cachedEvent = eventCacheRef.current.get(eventId);
    eventCacheRef.current.delete(eventId);

    const operationPromise = (async () => {
      retryInProgressRef.current = true;
      try {
        await sendPinnedStateWithRetry(client, roomId, newIds, eventId, 'unpin', isMountedRef);
        logger.info('Unpinned event:', eventId);
      } catch (err) {
        // Rollback on error
        logger.error('Error unpinning event, rolling back:', err);
        if (isMountedRef.current) {
          setPinnedEventIds(previousIds);
          // Restore cache entry if it existed
          if (cachedEvent) {
            eventCacheRef.current.set(eventId, cachedEvent);
          }
        }
        throw err;
      } finally {
        retryInProgressRef.current = false;
        if (pinOperationInFlightMap.get(roomId) === operationPromise) {
          pinOperationInFlightMap.delete(roomId);
        }
      }
    })();

    pinOperationInFlightMap.set(roomId, operationPromise);
    return operationPromise;
  }, [client, roomId, canPin, pinnedEventIds]);

  /**
   * Toggle pin state for an event
   * @param {string} eventId - The event ID to toggle
   * @returns {Promise<void>}
   */
  const togglePin = useCallback(async (eventId) => {
    if (pinnedEventIds.includes(eventId)) {
      await unpinEvent(eventId);
    } else {
      await pinEvent(eventId);
    }
  }, [pinnedEventIds, pinEvent, unpinEvent]);

  return {
    // State
    pinnedEventIds,
    pinnedEvents,       // All loaded events
    displayedEvents,    // Sliced events for lazy loading
    isLoading,
    error,
    canPin,

    // Computed
    pinnedCount,        // Excludes redacted messages for accurate badge count
    hasMore,            // Whether more events can be loaded

    // Actions
    pinEvent,
    unpinEvent,
    togglePin,
    loadMore,           // Load more events for lazy loading
  };
};

export default usePinnedMessages;
